/**
 * Subscription — Algomed SubscriptionCheckoutPage hiyerarşisinin
 * Telegram WebApp adaptasyonu.
 *
 * Mirror:
 * - Tier başlık + interval badge (Checkout HStack: Heading + Badge)
 * - Büyük fiyat (formatMoneyCompact muadili — burada ⭐ Stars)
 * - İçerik özeti satırı: "Lectures · Questions · Flashcards · AlgoSpots"
 * - Tier kartları (Silver/Gold/Diamond) — Algomed StorePage tier-card hiyerarşisi
 * - "Stars satın al" CTA + "Web sitesinden yönet" alt link
 *
 * Telegram MainButton: dominant action — `tier=free` ise "Planı Yükselt",
 * aktif aboneliği varsa "Abonelik Yönet" (web'e yönlendirir).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Divider,
  Flex,
  HStack,
  Heading,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { api } from '../lib/api';
import { tg, setMainButton, haptic } from '../telegram';
import { BRAND, ICON, type Tier } from '@algobot/shared';
import PlanBadge from '../components/PlanBadge';

// `Me` tipi lib/api.ts'deki canonical `MeResp` ile birebir ayni — duplicate
// yerine import et. Boylece subscriptionEndsAt ve algomedUserId gibi yeni
// alanlar otomatik kullanilabilir hale gelir.
import type { MeResp as Me } from '../lib/api';

interface UsageRow {
  feature: string;
  used: number;
  limit: number | null;
  resetAt: string;
}

interface PlanDef {
  sku: string;
  tier: Tier;
  title: string;
  intervalLabel: string;
  stars: number;
  /** İçerik özeti — Algomed Checkout `formatContentSummary` muadili. */
  contentSummary: { lectures: string; questions: string; flashcards: string; algospots: string };
  perks: string[];
  scheme: string;
}

const PLANS: PlanDef[] = [
  {
    sku: 'pass:silver:30',
    tier: 'silver',
    title: 'Silver',
    intervalLabel: 'Aylık · 30 gün',
    stars: 1500,
    contentSummary: {
      lectures: '3 / gün',
      questions: 'sınırsız',
      flashcards: 'sınırsız',
      algospots: 'sınırsız',
    },
    perks: [
      'Sınırsız soru bankası',
      'Sınırsız flashcard',
      'Sınırsız AlgoSpot',
      'Günlük 3 konu anlatımı',
    ],
    scheme: 'gray',
  },
  {
    sku: 'pass:gold:30',
    tier: 'gold',
    title: 'Gold',
    intervalLabel: 'Aylık · 30 gün',
    stars: 2500,
    contentSummary: {
      lectures: 'sınırsız',
      questions: 'sınırsız',
      flashcards: 'sınırsız',
      algospots: 'sınırsız',
    },
    perks: [
      'Silver içerik + sınırsız konu anlatımı',
      'Konu sesli (TTS)',
      'Live Arena & Wordle',
    ],
    scheme: 'yellow',
  },
  {
    sku: 'pass:diamond:30',
    tier: 'diamond',
    title: 'Diamond',
    intervalLabel: 'Aylık · 30 gün',
    stars: 4000,
    contentSummary: {
      lectures: 'sınırsız',
      questions: 'sınırsız',
      flashcards: 'sınırsız',
      algospots: 'sınırsız',
    },
    perks: [
      'Gold + Dijital Mentor V2',
      'Sınav hazırlık skoru',
      'Akran karşılaştırma',
      'Tüm premium özellikler',
    ],
    scheme: 'purple',
  },
];

const ACCOUNT_URL = 'https://algomed.app/hesabim/abonelik';

export default function SubscriptionPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [usage, setUsage] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [submittingSku, setSubmittingSku] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, u] = await Promise.all([
        api.me() as Promise<Me>,
        api.usage().catch(() => [] as UsageRow[]),
      ]);
      setMe(m);
      setUsage(u || []);
    } catch (err: any) {
      const status = err?.status as number | undefined;
      setError({
        status,
        message:
          status === 401
            ? 'Bu mini uygulama Telegram içinde açılmalı.'
            : 'Plan bilgileri alınamadı.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const tier = (me?.tier || 'free').toLowerCase() as Tier;
  const hasActivePlan = tier !== 'free';

  // MainButton: dominant action — Telegram bottom button. Algomed Checkout'taki
  // büyük "Aboneliği Başlat" butonunun mini-app eşdeğeri.
  useEffect(() => {
    if (loading || error) return;
    const text = hasActivePlan ? 'Abonelik Yönet' : BRAND.copy.upgrade;
    const cleanup = setMainButton(text, () => {
      haptic('select');
      if (hasActivePlan) {
        try {
          (window as any).Telegram?.WebApp?.openLink?.(ACCOUNT_URL);
        } catch {
          window.location.href = ACCOUNT_URL;
        }
      } else {
        // Mini-app içinde Stars purchase desteklenmiyor — bot'a yönlendir.
        tg()?.close();
      }
    });
    return cleanup;
  }, [loading, error, hasActivePlan]);

  const usageSummary = useMemo(() => {
    const map: Record<string, UsageRow> = {};
    for (const u of usage) map[u.feature] = u;
    return map;
  }, [usage]);

  const buy = (sku: string) => {
    haptic('select');
    setSubmittingSku(sku);
    // Stars purchase Bot ContextMessage gerektirir → kullanıcıyı bota yönlendir.
    setTimeout(() => {
      tg()?.close();
      setSubmittingSku(null);
    }, 100);
  };

  if (loading) {
    return (
      <Container maxW="md" py={10}>
        <VStack spacing={3}>
          <Spinner size="xl" color="accent" thickness="3px" />
          <Text color="textSecondary" fontSize="sm">
            Algomed yükleniyor…
          </Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="md" py={6}>
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          borderRadius="xl"
          py={6}
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
        >
          <AlertIcon boxSize="32px" color="warningIcon" />
          <AlertTitle mt={3} fontSize="md" color="textPrimary">
            {error.status === 401 ? 'Telegram içinde aç' : 'Bağlanılamadı'}
          </AlertTitle>
          <AlertDescription fontSize="sm" color="textSecondary" mt={2}>
            {error.message}
          </AlertDescription>
          <Button mt={4} colorScheme="brand" size="sm" onClick={load}>
            Tekrar dene
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={4} px={3}>
      <VStack spacing={4} align="stretch">
        {/* HEADER */}
        <VStack align="start" spacing={1}>
          <HStack spacing={2}>
            <Text fontSize="xl" lineHeight="1" color="accent">
              {ICON.payment}
            </Text>
            <Heading size="md" color="textPrimary">
              Abonelik
            </Heading>
          </HStack>
          <Text color="textSecondary" fontSize="sm">
            Telegram Stars ile öde, anında aktif olur. İstediğin zaman iptal edebilirsin.
          </Text>
        </VStack>

        {/* CURRENT PLAN */}
        <Card variant="elevated">
          <CardBody>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between" align="center">
                <HStack spacing={2}>
                  <Text
                    fontSize="11px"
                    color="textMuted"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                  >
                    Mevcut Plan
                  </Text>
                </HStack>
                <PlanBadge tier={tier} size="md" />
              </HStack>
              <Text fontSize="lg" fontWeight="bold" color="textPrimary">
                {hasActivePlan ? `${tier.toUpperCase()} aktif` : 'Ücretsiz plan'}
              </Text>
              {hasActivePlan && me?.subscriptionEndsAt ? (
                <Text fontSize="xs" color="textSecondary">
                  📅 {formatTrDate(me.subscriptionEndsAt)} tarihine kadar geçerli
                </Text>
              ) : null}
              {!hasActivePlan && usageSummary.question_bank_daily ? (
                <Text fontSize="xs" color="textSecondary">
                  Bugün {usageSummary.question_bank_daily.used}/
                  {usageSummary.question_bank_daily.limit ?? '∞'} soru ·{' '}
                  {usageSummary.flashcard_daily?.used ?? 0}/
                  {usageSummary.flashcard_daily?.limit ?? '∞'} flashcard
                </Text>
              ) : null}
            </VStack>
          </CardBody>
        </Card>

        {/* PLAN CARDS */}
        {PLANS.map((p) => {
          const isCurrent = tier === p.tier;
          return (
            <Card
              key={p.sku}
              variant="elevated"
              borderColor={isCurrent ? 'accent' : 'borderPrimary'}
              borderWidth="1px"
            >
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between" align="center" flexWrap="wrap">
                    <HStack spacing={2}>
                      <Text fontSize="2xl" lineHeight="1" color="accent">
                        {BRAND.emojiPlan[p.tier]}
                      </Text>
                      <VStack align="start" spacing={0}>
                        <Heading size="md" color="textPrimary">
                          {p.title}
                        </Heading>
                        <Badge colorScheme={p.scheme} fontSize="10px" textTransform="none">
                          {p.intervalLabel}
                        </Badge>
                      </VStack>
                    </HStack>
                    <VStack align="end" spacing={0}>
                      <HStack align="baseline" spacing={1}>
                        <Text fontWeight="extrabold" fontSize="2xl" color="textPrimary">
                          {p.stars}
                        </Text>
                        <Text fontSize="sm" color="accent" fontWeight="bold">
                          ⭐
                        </Text>
                      </HStack>
                      <Text fontSize="11px" color="textMuted">
                        Telegram Stars
                      </Text>
                    </VStack>
                  </HStack>

                  {/* Content summary · Algomed Checkout formatContentSummary */}
                  <Box
                    bg="bgSecondary"
                    borderWidth="1px"
                    borderColor="borderPrimary"
                    borderRadius="md"
                    px={3}
                    py={2}
                  >
                    <SimpleGrid columns={2} spacing={1}>
                      <ContentRow label="Konu Anlatımı" value={p.contentSummary.lectures} />
                      <ContentRow label="Soru" value={p.contentSummary.questions} />
                      <ContentRow label="Flashcard" value={p.contentSummary.flashcards} />
                      <ContentRow label="AlgoSpot" value={p.contentSummary.algospots} />
                    </SimpleGrid>
                  </Box>

                  <VStack align="start" spacing={1}>
                    {p.perks.map((perk) => (
                      <HStack key={perk} spacing={2} align="start">
                        <Text color="successIcon" fontSize="sm" lineHeight="1.2" pt={0.5}>
                          {ICON.ok}
                        </Text>
                        <Text fontSize="sm" color="textSecondary" lineHeight="1.3">
                          {perk}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  <Divider borderColor="borderPrimary" />

                  <HStack spacing={2} align="center">
                    <Text fontSize="sm" lineHeight="1" color="textMuted">
                      {ICON.lock}
                    </Text>
                    <Text fontSize="11px" color="textMuted">
                      Telegram Stars güvenli ödeme · iptal her zaman
                    </Text>
                  </HStack>

                  {isCurrent ? (
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme="brand"
                      isDisabled
                      w="full"
                    >
                      Aktif Plan
                    </Button>
                  ) : (
                    <Button
                      size="md"
                      colorScheme="brand"
                      w="full"
                      isLoading={submittingSku === p.sku}
                      onClick={() => buy(p.sku)}
                    >
                      Stars satın al
                    </Button>
                  )}
                </VStack>
              </CardBody>
            </Card>
          );
        })}

        {/* WEB MANAGEMENT LINK */}
        <Flex justify="center" pt={1}>
          <Link
            href={ACCOUNT_URL}
            isExternal
            fontSize="xs"
            color="textSecondary"
            textDecoration="underline"
            onClick={(e) => {
              e.preventDefault();
              haptic('select');
              try {
                (window as any).Telegram?.WebApp?.openLink?.(ACCOUNT_URL);
              } catch {
                window.location.href = ACCOUNT_URL;
              }
            }}
          >
            Web sitesinden yönet →
          </Link>
        </Flex>
      </VStack>
    </Container>
  );
}

/**
 * Backend'in dondurdugu ISO tarih string'ini "5 Mayıs 2026" formatina cevir.
 */
function formatTrDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function ContentRow({ label, value }: { label: string; value: string }) {
  return (
    <HStack justify="space-between" minW={0}>
      <Text fontSize="11px" color="textMuted" noOfLines={1}>
        {label}
      </Text>
      <Text fontSize="11px" color="textPrimary" fontWeight="semibold" noOfLines={1}>
        {value}
      </Text>
    </HStack>
  );
}
